import { useEffect, useState } from "react";
import {
  AccountingPeriods,
  taskStatuses,
  taskStatusesColors,
} from "../../entities/ITask";
import useBaseRequest from "../../api/BaseRequest";
import TaskGateway from "../../api/gateways/TaskGateway";
import { useToast } from "../../components/toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TableRow } from "../../components/table";
import moment from "moment";
import { Icon } from "../../modules/icon";
import { screens } from "../Navigation.types";
import { Badge } from "../../components/badge";
import { BadgeTypes } from "../../components/badge/Badge.types";
import { useUser } from "../../store/auth/AuthSelectors";

const useMyTasks = () => {
  const { danger, success } = useToast();
  const navigate = useNavigate();
  const onRowPress = (id: number) => {
    navigate(screens.task.root.replace(":id", id.toString()));
  };
  const userId = useUser()?.id || -1;
  const [tasks, setTasks] = useState<TableRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const location = useLocation();

  useEffect(() => {
    if (userId !== -1) {
      const params = new URLSearchParams(location.search);
      if (!params.has("skip")) params.set("skip", "0");
      if (!params.has("limit")) params.set("limit", "10");
      params.append(
        "filters",
        JSON.stringify({
          name: "user=>id",
          type: "number",
          operator: "eq",
          value: JSON.stringify(userId),
        })
      );
      getData("?" + params.toString());
    }
  }, [location.search, userId]);

  const {
    execute: getData,
    loading,
    data,
  } = useBaseRequest(TaskGateway.getTasks, {
    onCompleted: (data) => {
      if (data) {
        const taskTable: TableRow[] = [];

        data.tasks.forEach((item) => {
          taskTable.push([
            item.id,
            item.document.metaData.mb || item.document.metaData.jmbg || "",
            item.document.metaData.msisdn,
            item.document.metaData.documentCategory +
              "-" +
              item.document.metaData.documentType,

            AccountingPeriods[item.taskTypeCode],

            <Badge
              type={taskStatusesColors[item.status] as BadgeTypes}
              className="fw-bold"
            >
              {taskStatuses[item.status]}
            </Badge>,
            <Link
              to={screens.basketTasks.replace(":id", item.basketId.toString())}
            >
              {data.baskets.find((it) => it.id === item.basketId)?.name || ""}
            </Link>,
            item.complaintNumber || "",
            item.customerName || "",
            moment(item.createdAt).format("DD.MM.YYYY"),
            <div>
              <Link to={screens.task.root.replace(":id", item.id.toString())}>
                <Icon
                  className="mr-3"
                  title="See task's info"
                  type="fa-solid fa-circle-info"
                  color="black"
                  size={16}
                />
              </Link>
              <Link
                to={screens.documents + `/${item.document.id}`}
                title="See document info"
              >
                <Icon
                  className="mr-3"
                  type="fa-solid fa-file"
                  color="black"
                  size={16}
                />
              </Link>
              {!!item.comments.length && (
                <Link
                  to={
                    screens.task.root.replace(":id", item.id.toString()) +
                    `/comments`
                  }
                  title={
                    "Last comment: " +
                    item.comments[item.comments.length - 1].text
                  }
                >
                  <Icon type="fa-solid fa-message" color="black" size={16} />
                </Link>
              )}
            </div>,
          ]);
        });
        setTasks(taskTable);
        setTotal(data.count);
      }
    },
    onError: (error) => {
      danger(error?.message || "Something went wrong.");
    },
  });

  //Bulk actions
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [selectedAction, setSelectedAction] = useState<"" | "assign" | "move">(
    ""
  );
  const { execute: reassignBulkTasks, loading: reassigningTasks } =
    useBaseRequest(TaskGateway.reassignBulkTasks, {
      onCompleted: () => {
        setSelectedTasks([]);
        setSelectedAction("");
        success("Tasks successfully reassigned.");
        getData(
          (location.search || "?") +
            `&filters=${JSON.stringify({
              name: "user=>id",
              type: "number",
              operator: "eq",
              value: JSON.stringify(userId),
            })}`
        );
      },
      onError: (error) => {
        danger(error?.message || "Something went wrong.");
      },
    });
  const { execute: moveBulkTasks, loading: movingTasks } = useBaseRequest(
    TaskGateway.moveToAnotherBasketBulkTasks,
    {
      onCompleted: () => {
        setSelectedTasks([]);
        setSelectedAction("");
        success("Tasks successfully moved.");
        getData(
          (location.search || "?") +
            `&filters=${JSON.stringify({
              name: "user=>id",
              type: "number",
              operator: "eq",
              value: JSON.stringify(userId),
            })}`
        );
      },
      onError: (error) => {
        danger(error?.message || "Something went wrong.");
      },
    }
  );

  return {
    myTasks: tasks,
    total,
    getData,
    onRowPress,
    loading,
    selectedTasks,
    setSelectedTasks,
    selectedAction,
    setSelectedAction,
    reassignBulkTasks,
    reassigningTasks,
    moveBulkTasks,
    movingTasks,
  };
};

export default useMyTasks;
